import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import type { CartItemPrice } from '../contexts/TurnAuthoritySnapshotContext'
import { isExactCartPanelPrice } from '../components/ListView/tripListCartPrices'
import { getCartItemDisplayPrice } from './cartItemPriceSelection'
import { countsTowardCartTotal, describeCartCheckoutBlocker, shouldIncludeCartPanelItemPriceEstimate, type CartPanelItemCartStatus } from './cartPanelUtils'

export interface CartItemAssistantPriceInput {
  tripItemId: number
  pricing: {
    price: number | undefined
    memberPrice: number | undefined
  }
  // Same fields computeCartPanelTotal filters on -- an item can be priced but not
  // count toward the total the customer is actually quoted (customer-excluded, or
  // not yet available to book). Without surfacing this, the assistant would sum
  // every priced item and reproduce the exact "checkout price differs from the AI
  // quote" bug this fact exists to prevent, just by over-counting instead.
  cartExcluded: boolean
  // The item's own raw flag, not yet the derived eligibility used by the real total --
  // matches CartPanelItem's own field exactly (always defined in production; there is
  // no real state where a cart panel item lacks this). See
  // shouldIncludeCartPanelItemPriceEstimate below, which both useCartPanel and
  // useCartButtonSummary apply to this same field before it ever reaches
  // countsTowardCartTotal. Using it unvalidated here would let this fact disagree with
  // the total the customer is actually shown, e.g. under-reporting a saved-but-not-yet-
  // cart-active accommodation that the navbar/cart panel total already includes.
  includeInTotal: boolean
  productType: 'accommodation' | 'experience' | 'flight'
  // Same source isExactCartPanelPrice uses to decide the List row's own "From" prefix --
  // 'cart' means this item is backed by a real multi-cart item, 'itinerary' means it's
  // only a saved/shortlisted item with no active cart entry yet.
  source: 'cart' | 'itinerary'
  // Paired with source above so isExactCartPanelPrice can decide whether this item is
  // fully configured in an active cart entry (see isBookingReady on CartItemPrice); also
  // the same source the cart panel tiles use to decide which warning banner to show,
  // letting the assistant answer "why can't I check out yet" without guessing or having
  // to separately call a cart-reading tool for something already computed right here.
  cartStatus: CartPanelItemCartStatus
}

export interface BuildCartItemAssistantPricesOptions {
  // Whether the List view's own LuxPlus preview toggle is on -- any customer can flip this
  // regardless of real membership, so it may not match what checkout will actually charge.
  luxPlusEnabled: boolean
  // Real LuxPlus entitlement, matching the cart panel and navbar cart total.
  hasLuxPlusBenefits: boolean
  currentCurrency: string
  currentRegionCode: string
}

/**
 * Sends both prices rather than picking one: the assistant may be asked either "what does
 * the screen show" (displayedPrice, toggle-driven) or "what will I actually pay"
 * (checkoutPrice, real-entitlement-driven), and those two can genuinely differ for a
 * non-member previewing member pricing. Guessing which one the customer means and only
 * sending that risks reproducing the "checkout price differs from the AI quote" bug this
 * fact exists to prevent, just from the opposite direction.
 */
export function buildCartItemAssistantPrices(
  items: Array<CartItemAssistantPriceInput>,
  options: BuildCartItemAssistantPricesOptions,
): Array<CartItemPrice> {
  const { luxPlusEnabled, hasLuxPlusBenefits, currentCurrency, currentRegionCode } = options

  return items.flatMap((item) => {
    // Not `item.pricing.price === undefined` -- getCartItemDisplayPrice can now produce a
    // valid price from memberPrice alone when there's no base price at all (see its own
    // fallback), so gating on price alone here would discard an item this fact should
    // still be able to report, before that fallback ever gets a chance to run.
    const displayedPrice = getCartItemDisplayPrice(item.pricing, luxPlusEnabled)
    const checkoutPrice = getCartItemDisplayPrice(item.pricing, hasLuxPlusBenefits)
    if (displayedPrice === undefined || checkoutPrice === undefined) return []

    const countsTowardTotal = countsTowardCartTotal({
      cartExcluded: item.cartExcluded,
      includeInTotal: shouldIncludeCartPanelItemPriceEstimate(item),
    })
    const checkoutBlocker = describeCartCheckoutBlocker(item.cartStatus)

    return [{
      itemId: String(item.tripItemId),
      displayedPrice: formatCurrency(displayedPrice, currentCurrency, currentRegionCode),
      checkoutPrice: formatCurrency(checkoutPrice, currentCurrency, currentRegionCode),
      countsTowardTotal,
      isBookingReady: isExactCartPanelPrice(item),
      ...(checkoutBlocker !== undefined ? { checkoutBlocker } : {}),
    }]
  })
}
